import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, NgSelectOption, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { CKEDITORCONFIGMINIMAL, CKEDITORCONFIGMINIMALWITHLINK } from '../ckeditor-config';
import { Currency } from '../currency';
import { HttpService } from '../service/http.service';
import { SalesProductType } from '../sales-product-type';
import * as moment from 'moment';
import Decimal from 'decimal.js';
import { ToastMessageService } from '../toast-message.service';
import { QuotationTemplateProduct } from '../quotation-template-product';
import { Language } from '../language';
import { QuotationTemplate } from '../quotation-template';
import { QuotationTemplateWithDetail } from '../quotation-template-with-detail';
import { SchoolListItem } from '../school-list-item';
import { ConfirmationService } from 'primeng/api';
import * as Editor from '../../libs/ckeditor';
import { QuotationTemplateService } from '../quotation-template.service';

@Component({
  selector: 'app-edit-quotation-template',
  templateUrl: './edit-quotation-template.component.html',
  styleUrls: ['./edit-quotation-template.component.css']
})
export class EditQuotationTemplateComponent implements OnInit {

  templateForm!: FormGroup;
  productTypes: SalesProductType[] = [];
  currencies: Currency[] = [];
  languageList: Language[] = [];
  schoolList: SchoolListItem[] = [];
  selectedTemplate!: QuotationTemplate;
  productsToDelete: QuotationTemplateProduct[] = [];

  productPriceInCurrency: Observable<string>[] = [];
  productPriceInSecondCurrency: Observable<string>[] = [];

  editorConfig = CKEDITORCONFIGMINIMAL;
  descriptionEditorConfig = CKEDITORCONFIGMINIMALWITHLINK;

  yearRange = (moment().year() - 10) + ":" + (moment().year() + 4);

  public Editor = Editor;

  @Input("template") inputTemplate?: QuotationTemplate;
  @Output() updatedTemplate = new EventEmitter<QuotationTemplateWithDetail>();

  constructor(
    private httpService: HttpService,
    private route: ActivatedRoute,
    private builder: FormBuilder,
    private toastService: ToastMessageService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private quotationTemplateService: QuotationTemplateService
  ) { }

  ngOnInit(): void {
    this.httpService.getCurrencies().subscribe(currencies => {
      this.currencies = currencies;
    });
    this.httpService.getLanguages().subscribe(res => this.languageList = res);
    this.httpService.getSchools().subscribe(res => this.schoolList = res);
    this.httpService.getSalesProductTypes().subscribe(res => this.productTypes = res);

    //if there is no input template, fetch the template with routing template id
    if(!this.inputTemplate){
      let templateId = Number(this.route.snapshot.paramMap.get("templateId"));
      this.httpService.getQuotationTemplateById(templateId).subscribe(res => {
        this.selectedTemplate = res;
        this.initTemplateForm();
      });
    }
    else {
      this.selectedTemplate = this.inputTemplate;
      this.initTemplateForm();
    }

  }

  initTemplateForm(){
    this.templateForm = this.builder.group({
      name: this.selectedTemplate.name,
      description: this.selectedTemplate.description,
      comment: this.selectedTemplate.comment,
      products: this.builder.array([]),
      baseCurrency: this.selectedTemplate.baseCurrency,
      secondCurrency: this.selectedTemplate.secondCurrency,

      priceIncludes: this.selectedTemplate.priceIncludes,
      priceNotIncludes: this.selectedTemplate.priceNotIncludes,
      school: this.selectedTemplate.school,
      language: this.selectedTemplate.language
    });
    this.initProducts();
  }

  mouseover(i: number) {
    let array = this.templateForm.get("products") as FormArray;
    array.controls[i].get("activeMenu")!.setValue(true);
  }
  mouseout(i: number) {
    let array = this.templateForm.get("products") as FormArray;
    array.controls[i].get("activeMenu")!.setValue(false);
  }

  moveItem(array: any[], i: number, delta: number) {
    let newIndex = i + delta;
    if (newIndex < 0 || newIndex >= array.length) {
      return;
    }
    let indexes = [i, newIndex].sort();
    array.splice(indexes[0], 2, array[indexes[1]], array[indexes[0]]);
  }

  moveUp(i: number) {
    let itemArray = this.templateForm.get("products") as FormArray;
    this.moveItem(itemArray.controls, i, -1);
  }

  moveDown(i: number) {
    let itemArray = this.templateForm.get("products") as FormArray;
    this.moveItem(itemArray.controls, i, 1);
  }

  insertAt(array: AbstractControl[], index: number, element: FormGroup) {
    array.splice(index, 0, element);
  }

  insertItem(i: number) {
    let itemArray = this.templateForm.get("products") as FormArray;
    this.insertAt(itemArray.controls, i + 1, this.builder.group({
      type: this.productTypes[0],
      description: "",
      amount: [new Decimal(0.00), Validators.pattern(/^-?[.\d]+$/)],
      currency: new Currency(1, "Euro", "EUR", "€"),
      activeMenu: false,
      id: null
    }));
  }

  removeItem(i: number) {
    let itemArray = this.templateForm.get("products") as FormArray;
    if(itemArray.value[i].id != null && itemArray.value[i].id != undefined){
      this.productsToDelete.push(itemArray.value[i]);
    }
    itemArray.controls.splice(i, 1);
  }

  getProductList() {
    let products = [];
    let productFormArray = this.templateForm.get("products") as FormArray;
    for (let product of productFormArray.controls) {
      products.push(new QuotationTemplateProduct(
        product.get("type")!.value,
        product.get("description")!.value,
        new Decimal(product.get("amount")!.value),
        product.get("currency")!.value,
        product.get("id")!.value
      ));
    }
    return products;
  }


  initProducts() {
    let array = this.templateForm.get("products") as FormArray;

    for (let product of this.selectedTemplate.products) {
      array.controls.push(this.builder.group({
        type: product.productType,
        description: product.description,
        amount: product.amount.toFixed(2),
        currency: product.currency,
        activeMenu: false,
        id: product.id
      }));
    }
  }

  updateQuotationTemplate(){
    this.selectedTemplate.description = this.templateForm.value.description;
    this.selectedTemplate.name = this.templateForm.value.name;
    this.selectedTemplate.comment = this.templateForm.value.comment;
    this.selectedTemplate.products = this.getProductList();
    this.selectedTemplate.baseCurrency = this.templateForm.value.baseCurrency;
    this.selectedTemplate.secondCurrency = this.templateForm.value.secondCurrency;
    this.selectedTemplate.priceIncludes = this.templateForm.value.priceIncludes;
    this.selectedTemplate.priceNotIncludes = this.templateForm.value.priceNotIncludes;
    this.selectedTemplate.school = this.templateForm.value.school;
    this.selectedTemplate.language = this.templateForm.value.language;
    let templateWithDetail = new QuotationTemplateWithDetail(
      this.selectedTemplate, this.productsToDelete
    );
    this.httpService.putQuotationTemplate(templateWithDetail).subscribe(res => {
      this.selectedTemplate = res;
      this.productsToDelete = [];
      this.toastService.addSuccess("Success", "Updated");
      this.updatedTemplate.emit(templateWithDetail);
      this.quotationTemplateService.activateEditDialog = false;
    }, error => this.toastService.addError("Error", error.message));
  }

  archiveQuotationTemplate(){
    this.confirmationService.confirm({
      message: "Are you sure that you want to delete this template?",
      accept: () => {
        this.httpService.archiveQuotationTemplate(this.selectedTemplate.id!).subscribe(res => {
          this.router.navigateByUrl("/management/quotation-templates");
        }, error => this.toastService.addError("Error", error.message));
      }
    });
  }

  get productFormArray(){
    let array = this.templateForm.get('products') as FormArray;
    return array.controls;
  }

  isProductActive(index: number){
    let array = this.templateForm.get('products') as FormArray;
    return array.controls![index].get('activeMenu')!.value;
  }

}
